import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '../ui/Dialog';
import { authAPI } from '../../services/api';

interface PhoneAuthProps {
  onSuccess: (userData: any) => void;
  onCancel?: () => void;
  gameId?: string; // If provided, will link game after auth
}

export default function PhoneAuth({ onSuccess, onCancel, gameId }: PhoneAuthProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);

  // Format phone number as user types
  const formatPhoneInput = (value: string): string => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');

    // Format as +1 (XXX) XXX-XXXX
    if (digits.length === 0) return '';
    if (digits.length <= 1) return `+${digits}`;
    if (digits.length <= 4) return `+${digits.slice(0, 1)} (${digits.slice(1)}`;
    if (digits.length <= 7) return `+${digits.slice(0, 1)} (${digits.slice(1, 4)}) ${digits.slice(4)}`;
    return `+${digits.slice(0, 1)} (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 11)}`;
  };

  const handleRequestOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!phoneNumber.trim()) {
      setError(t('auth.enterPhone'));
      return;
    }

    try {
      setIsLoading(true);

      // Format phone number: ensure it starts with +
      let formattedPhone = phoneNumber.trim().replace(/\D/g, ''); // Remove non-digits

      // If starts with 1 and has 11 digits, add +
      if (formattedPhone.length === 11 && formattedPhone.startsWith('1')) {
        formattedPhone = '+' + formattedPhone;
      }
      // If 10 digits (US number without country code), add +1
      else if (formattedPhone.length === 10) {
        formattedPhone = '+1' + formattedPhone;
      }
      // If doesn't start with +, add it
      else if (!formattedPhone.startsWith('+')) {
        formattedPhone = '+' + formattedPhone;
      } else {
        formattedPhone = '+' + formattedPhone;
      }

      const response = await authAPI.requestOTP(formattedPhone);

      // Update phoneNumber with formatted version for verification step
      setPhoneNumber(formattedPhone);
      setExpiresAt(new Date(response.expiresAt));
      setStep('code');
    } catch (error: any) {
      setError(error.response?.data?.error || t('auth.sendError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!code.trim()) {
      setError(t('auth.enterCode'));
      return;
    }

    try {
      setIsLoading(true);

      const response = await authAPI.verifyOTP(phoneNumber, code);

      // If gameId provided, link it to user
      if (gameId && response.user.id) {
        await authAPI.linkGame(response.user.id, gameId, 'creator');
      }

      onSuccess(response);
    } catch (error: any) {
      setError(error.response?.data?.error || t('auth.invalidCode'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setStep('phone');
    setCode('');
    setError(null);
  };

  return (
    <Dialog
      title={step === 'phone' ? t('auth.phoneAuth') : t('auth.code')}
      labelledBy="phone-auth-title"
      onClose={() => onCancel?.()}
      busy={isLoading}
      maxWidth="450px"
      padding="2.5rem"
    >
      <div>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div aria-hidden="true" style={{ fontSize: '3rem', marginBottom: '1rem' }}>📱</div>
          <h2 id="phone-auth-title" style={{ fontSize: '1.8rem', marginBottom: '0.5rem', color: '#e2e8f0' }}>
            {step === 'phone' ? t('auth.phoneAuth') : t('auth.code')}
          </h2>
          <p style={{ color: '#a0aec0', fontSize: '0.9rem' }}>
            {step === 'phone'
              ? t('auth.phoneAuthSubtitle')
              : t('auth.codeSubtitle', { phone: phoneNumber })}
          </p>
        </div>

        {error && (
          <div id="auth-error" role="alert" style={{
            padding: '1rem',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '12px',
            color: '#fca5a5',
            marginBottom: '1.5rem',
            fontSize: '0.9rem',
          }}>
            {error}
          </div>
        )}

        {step === 'phone' ? (
          <form onSubmit={handleRequestOTP}>
            <div style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="auth-phone" style={{
                display: 'block',
                marginBottom: '0.75rem',
                fontWeight: '600',
                fontSize: '0.95rem',
                color: '#cbd5e0',
              }}>
                {t('auth.phone')}
              </label>
              <input
                id="auth-phone"
                type="tel"
                // autocomplete="tel" is what lets a phone offer the saved number instead of
                // making the user type it blind on a touch keyboard.
                autoComplete="tel"
                inputMode="tel"
                aria-invalid={!!error}
                aria-describedby={[error ? 'auth-error' : null, 'auth-phone-hint'].filter(Boolean).join(' ') || undefined}
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(formatPhoneInput(e.target.value))}
                placeholder="+1 (555) 123-4567"
                disabled={isLoading}
                maxLength={18}
                style={{
                  width: '100%',
                  padding: '1rem',
                  fontSize: '1rem',
                  border: '1px solid rgba(99, 102, 241, 0.3)',
                  borderRadius: '12px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  color: '#e2e8f0',
                  outline: 'none',
                  transition: 'all 0.2s',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.6)';
                  e.currentTarget.style.background = 'rgba(0, 0, 0, 0.4)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.3)';
                  e.currentTarget.style.background = 'rgba(0, 0, 0, 0.3)';
                }}
              />
              <p id="auth-phone-hint" style={{ fontSize: '0.8rem', color: '#718096', marginTop: '0.5rem' }}>
                {t('auth.smsWillBeSent')}
              </p>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '1rem',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                border: 'none',
                borderRadius: '12px',
                background: isLoading
                  ? 'rgba(99, 102, 241, 0.3)'
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s',
                marginBottom: '1rem',
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 10px 25px rgba(102, 126, 234, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {isLoading ? `⏳ ${t('auth.sending')}` : t('auth.sendCodeIcon')}
            </button>

            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                style={{
                  width: '100%',
                  padding: '1rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  border: '1px solid rgba(99, 102, 241, 0.3)',
                  borderRadius: '12px',
                  background: 'transparent',
                  color: '#a0aec0',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.6)';
                  e.currentTarget.style.color = '#cbd5e0';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.3)';
                  e.currentTarget.style.color = '#a0aec0';
                }}
              >
                {t('auth.cancel')}
              </button>
            )}
          </form>
        ) : (
          <form onSubmit={handleVerifyOTP}>
            <div style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="auth-code" style={{
                display: 'block',
                marginBottom: '0.75rem',
                fontWeight: '600',
                fontSize: '0.95rem',
                color: '#cbd5e0',
              }}>
                {t('auth.code')}
              </label>
              <input
                id="auth-code"
                type="text"
                // one-time-code is the difference between the OS offering the SMS code above the
                // keyboard and the user having to leave the browser, listen to the message, and
                // hold six digits in their head.
                autoComplete="one-time-code"
                inputMode="numeric"
                pattern="[0-9]*"
                aria-invalid={!!error}
                aria-describedby={error ? 'auth-error' : undefined}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                disabled={isLoading}
                autoFocus
                style={{
                  width: '100%',
                  padding: '1rem',
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  letterSpacing: '0.5rem',
                  border: '1px solid rgba(99, 102, 241, 0.3)',
                  borderRadius: '12px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  color: '#e2e8f0',
                  outline: 'none',
                  transition: 'all 0.2s',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.6)';
                  e.currentTarget.style.background = 'rgba(0, 0, 0, 0.4)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.3)';
                  e.currentTarget.style.background = 'rgba(0, 0, 0, 0.3)';
                }}
              />
              {expiresAt && (
                <p style={{ fontSize: '0.8rem', color: '#718096', marginTop: '0.5rem' }}>
                  {t('auth.codeValidFor')}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading || code.length !== 6}
              style={{
                width: '100%',
                padding: '1rem',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                border: 'none',
                borderRadius: '12px',
                background: (isLoading || code.length !== 6)
                  ? 'rgba(99, 102, 241, 0.3)'
                  : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: '#fff',
                cursor: (isLoading || code.length !== 6) ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s',
                marginBottom: '1rem',
              }}
              onMouseEnter={(e) => {
                if (!isLoading && code.length === 6) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 10px 25px rgba(16, 185, 129, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {isLoading ? `⏳ ${t('auth.verifying')}` : t('auth.verifyIcon')}
            </button>

            <button
              type="button"
              onClick={handleBack}
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '1rem',
                fontSize: '1rem',
                fontWeight: '600',
                border: '1px solid rgba(99, 102, 241, 0.3)',
                borderRadius: '12px',
                background: 'transparent',
                color: '#a0aec0',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.6)';
                  e.currentTarget.style.color = '#cbd5e0';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.3)';
                e.currentTarget.style.color = '#a0aec0';
              }}
            >
              {t('auth.back')}
            </button>
          </form>
        )}
      </div>
    </Dialog>
  );
}
